import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import productService, { CreateProductDto } from '../../services/productService';
import { chartOfAccountService } from '../../services/chartOfAccountService';
import toast from 'react-hot-toast';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52',
  outline: 'none', colorScheme: 'light', boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase',
  color: '#6b6a5c', marginBottom: 6,
};

const sectionHeading: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 500,
  letterSpacing: '0.2em', textTransform: 'uppercase', color: '#d97706',
  marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid rgba(92,78,51,0.15)',
};

interface ProductFormProps {
  onClose: () => void;
  onSuccess: () => void;
  productId?: string;
  initialData?: Partial<CreateProductDto>;
}

const ProductForm: React.FC<ProductFormProps> = ({ onClose, onSuccess, productId, initialData }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<CreateProductDto>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    itemType: initialData?.itemType || 'INVENTORY',
    unitPrice: initialData?.unitPrice || 0,
    costPrice: initialData?.costPrice || 0,
    isActive: initialData?.isActive ?? true,
    trackInventory: initialData?.trackInventory ?? true,
    isSerialized: initialData?.isSerialized ?? false,
    reorderPoint: initialData?.reorderPoint || 0,
    reorderQty: initialData?.reorderQty || 0,
    incomeAccountId: initialData?.incomeAccountId || '',
    cogsAccountId: initialData?.cogsAccountId || '',
    assetAccountId: initialData?.assetAccountId || '',
  });

  // Active accounts for this company, filtered per-select by accountType below.
  // These are per-product overrides — leaving one unset falls back to the product's
  // category mapping, then the company default, at journal-posting time.
  const { data: accountsResponse } = useQuery({
    queryKey: ['chart-of-accounts', 'for-product-form'],
    queryFn: () => chartOfAccountService.getChartOfAccounts({ isActive: true, limit: 500 }),
  });
  // Exclude group headers (accounts with children, e.g. the "Income"/"Assets" roots created by
  // the chart-of-accounts import) — only real postable leaf accounts belong in these pickers.
  const postableAccounts = (accountsResponse?.data || []).filter(a => !a.children || a.children.length === 0);
  const incomeAccounts = postableAccounts.filter(a => a.accountType === 'INCOME');
  const cogsAccounts = postableAccounts.filter(a => a.accountType === 'EXPENSE');
  const assetAccounts = postableAccounts.filter(a => a.accountType === 'ASSET');

  const getSkuPrefix = (itemType: string) => {
    switch (itemType) {
      case 'SERVICE': return 'CL';
      case 'INVENTORY': return 'IV';
      case 'SERIALIZED': return 'SL';
      default: return 'IV';
    }
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateProductDto) => productService.createProduct(data),
    onSuccess: () => {
      toast.success('Product created successfully!');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product-stats'] });
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create product');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<CreateProductDto>) => productService.updateProduct(productId!, data),
    onSuccess: () => {
      toast.success('Product updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update product');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Empty string means "unset" for these optional account overrides — send null so the FK is
    // cleared, not an invalid empty-string id.
    const payload = {
      ...formData,
      incomeAccountId: formData.incomeAccountId || null,
      cogsAccountId: formData.cogsAccountId || null,
      assetAccountId: formData.assetAccountId || null,
    } as CreateProductDto;
    if (productId) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleChange = (field: keyof CreateProductDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemTypeChange = (itemType: 'SERVICE' | 'INVENTORY' | 'SERIALIZED') => {
    setFormData(prev => ({
      ...prev,
      itemType,
      trackInventory: itemType !== 'SERVICE',
      isSerialized: itemType === 'SERIALIZED',
    }));
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const typeButtonStyle = (type: string, activeColor: string, activeBg: string): React.CSSProperties => ({
    padding: 16, border: formData.itemType === type
      ? `2px solid ${activeColor}`
      : '1px solid rgba(92,78,51,0.25)',
    background: formData.itemType === type ? activeBg : 'transparent',
    borderRadius: 6, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
    flex: 1,
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(7,7,15,0.85)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      zIndex: 50, overflowY: 'auto', paddingTop: 80, paddingBottom: 40, padding: '80px 16px 40px',
    }}>
      <div style={{
        background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)',
        borderRadius: 8, width: '100%', maxWidth: 640, overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 28px 16px', borderBottom: '1px solid rgba(92,78,51,0.15)',
          background: '#ffffff', position: 'sticky', top: 0, zIndex: 1,
        }}>
          <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 300, color: '#1c2033', margin: 0 }}>
            {productId ? 'Edit Product' : 'Create New Product'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
              color: '#6b6a5c', cursor: 'pointer', padding: '4px 8px', fontSize: 16, lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: '24px 28px' }}>
          {/* Basic Information */}
          <div>
            <div style={sectionHeading}>Basic Information</div>

            {/* Auto-generated SKU Info */}
            {!productId && (
              <div style={{
                background: 'rgba(241,245,249,1)', border: '1px solid rgba(92,78,51,0.22)',
                borderRadius: 6, padding: '10px 14px', marginBottom: 16,
              }}>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>
                  <span style={{ fontWeight: 500, color: '#d97706' }}>SKU:</span> Will be auto-generated as{' '}
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#fbbf24' }}>
                    {getSkuPrefix(formData.itemType)}XXXX
                  </span>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 4, marginBottom: 0 }}>
                    Based on item type: Service (CL), Inventory (IV), Serialized (SL)
                  </p>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Name */}
              <div>
                <label style={labelStyle}>
                  Product Name <span style={{ color: '#c95a5a' }}>*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  style={inputStyle}
                  placeholder="e.g., Laptop Dell XPS 15"
                />
              </div>

              {/* Description */}
              <div>
                <label style={labelStyle}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
                  placeholder="Detailed description of the product..."
                />
              </div>

              {/* Item Type */}
              <div>
                <label style={labelStyle}>
                  Item Type <span style={{ color: '#c95a5a' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    type="button"
                    onClick={() => handleItemTypeChange('SERVICE')}
                    style={typeButtonStyle('SERVICE', 'rgba(90,140,201,0.7)', 'rgba(90,140,201,0.08)')}
                  >
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase', color: formData.itemType === 'SERVICE' ? '#5a8cc9' : '#5b5a52', marginBottom: 4 }}>Service</div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>Non-inventory</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleItemTypeChange('INVENTORY')}
                    style={typeButtonStyle('INVENTORY', 'rgba(90,201,140,0.7)', 'rgba(90,201,140,0.08)')}
                  >
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase', color: formData.itemType === 'INVENTORY' ? '#5ac98c' : '#5b5a52', marginBottom: 4 }}>Inventory</div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>Track stock</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleItemTypeChange('SERIALIZED')}
                    style={typeButtonStyle('SERIALIZED', 'rgba(176,122,220,0.7)', 'rgba(176,122,220,0.08)')}
                  >
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase', color: formData.itemType === 'SERIALIZED' ? '#b07adc' : '#5b5a52', marginBottom: 4 }}>Serialized</div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>Unique serial #</div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <div style={sectionHeading}>Pricing</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={labelStyle}>
                  Unit Price (BD) <span style={{ color: '#c95a5a' }}>*</span>
                </label>
                <input
                  type="number"
                  step="0.001"
                  required
                  value={formData.unitPrice}
                  onChange={(e) => handleChange('unitPrice', parseFloat(e.target.value) || 0)}
                  style={inputStyle}
                  placeholder="0.000"
                />
              </div>
              <div>
                <label style={labelStyle}>
                  Cost Price (BD) <span style={{ color: '#c95a5a' }}>*</span>
                </label>
                <input
                  type="number"
                  step="0.001"
                  required
                  value={formData.costPrice}
                  onChange={(e) => handleChange('costPrice', parseFloat(e.target.value) || 0)}
                  style={inputStyle}
                  placeholder="0.000"
                />
              </div>
            </div>

            {/* Profit Margin Display */}
            {formData.unitPrice > 0 && formData.costPrice > 0 && (
              <div style={{
                background: 'rgba(241,245,249,1)', border: '1px solid rgba(92,78,51,0.22)',
                borderRadius: 6, padding: '10px 14px', marginTop: 12,
              }}>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>
                  <span style={{ fontWeight: 500, color: '#d97706' }}>Profit Margin:</span>{' '}
                  <span style={{ color: '#6bbf8a' }}>
                    {(((formData.unitPrice - formData.costPrice) / formData.unitPrice) * 100).toFixed(2)}%
                  </span>
                  {' '}(BD {(formData.unitPrice - formData.costPrice).toFixed(3)} profit per unit)
                </div>
              </div>
            )}
          </div>

          {/* Accounting */}
          <div>
            <div style={sectionHeading}>Accounting</div>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: -8, marginBottom: 16 }}>
              Optional per-product overrides for automatic journal entries. Leave unset to use this product's
              category mapping, or the company default, when it's sold or purchased.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>Income Account</label>
                <select
                  value={formData.incomeAccountId || ''}
                  onChange={(e) => handleChange('incomeAccountId', e.target.value || null)}
                  style={inputStyle}
                >
                  <option value="">— Use category / company default —</option>
                  {incomeAccounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                  ))}
                </select>
              </div>

              {formData.itemType !== 'SERVICE' && (
                <>
                  <div>
                    <label style={labelStyle}>COGS Account</label>
                    <select
                      value={formData.cogsAccountId || ''}
                      onChange={(e) => handleChange('cogsAccountId', e.target.value || null)}
                      style={inputStyle}
                    >
                      <option value="">— Use category / company default —</option>
                      {cogsAccounts.map((a) => (
                        <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>Asset Account</label>
                    <select
                      value={formData.assetAccountId || ''}
                      onChange={(e) => handleChange('assetAccountId', e.target.value || null)}
                      style={inputStyle}
                    >
                      <option value="">— Use category / company default —</option>
                      {assetAccounts.map((a) => (
                        <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Inventory Settings (only for INVENTORY and SERIALIZED) */}
          {formData.itemType !== 'SERVICE' && (
            <div>
              <div style={sectionHeading}>Inventory Settings</div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={labelStyle}>Reorder Point</label>
                  <input
                    type="number"
                    value={formData.reorderPoint}
                    onChange={(e) => handleChange('reorderPoint', parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                    placeholder="10"
                  />
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 4 }}>
                    Alert when stock falls below this level
                  </p>
                </div>
                <div>
                  <label style={labelStyle}>Reorder Quantity</label>
                  <input
                    type="number"
                    value={formData.reorderQty}
                    onChange={(e) => handleChange('reorderQty', parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                    placeholder="50"
                  />
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 4 }}>
                    Suggested reorder amount
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
              style={{ accentColor: '#d97706', width: 16, height: 16 }}
            />
            <label htmlFor="isActive" style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52', cursor: 'pointer' }}>
              Active (product is available for sale)
            </label>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 12, paddingTop: 16, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1, padding: '10px 24px', background: 'none',
                border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
                fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 400,
                letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                flex: 1, padding: '10px 24px',
                background: isLoading ? 'rgba(92,78,51,0.25)' : 'linear-gradient(135deg, #d97706, #b45309)',
                border: 'none', borderRadius: 4,
                fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500,
                letterSpacing: '0.15em', textTransform: 'uppercase',
                color: isLoading ? '#c9c2ab' : '#1c2033',
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? 'Saving...' : productId ? 'Update Product' : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
